import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Toastify, { notifyError, notifySuccess } from "../../../utils/tostfy";
import MasterSuccessModal from '../MasterSuccessModal';
import { handleApiCall, getApiErrorMessage } from '../../../apis';
import { endpoint } from '../../../utils/endpoints';
import EditIcon from '../../../assets/image/edit-icon.svg'
import LeftAngle from '../../../assets/image/left-angle.svg'
import RightAngle from '../../../assets/image/right-angle.svg'
import PermissionGate from '../../../Components/PermissionGate/PermissionGate';
import { MODULES } from '../../../utils/permissions';
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../../utils/search";
import PageNumberInput from "../../../Components/Pagination/PageNumberInput";

/** Up to 6 digits before '.', then up to 2 fraction digits (e.g. 123456.78) */
const ODOMETER_INT_MAX = 6;
const ODOMETER_FRAC_MAX = 2;
const ODOMETER_INPUT_MAX_LEN = ODOMETER_INT_MAX + 1 + ODOMETER_FRAC_MAX; // 9

const sanitizeOdometerInput = (raw) => {
    let s = (raw ?? '').replace(/[^\d.]/g, '');
    const firstDot = s.indexOf('.');
    if (firstDot === -1) {
        return s.replace(/\D/g, '').slice(0, ODOMETER_INT_MAX);
    }
    const intPart = s
        .slice(0, firstDot)
        .replace(/\D/g, '')
        .slice(0, ODOMETER_INT_MAX);
    const afterFirst = s.slice(firstDot + 1);
    const secondDot = afterFirst.indexOf('.');
    const fracDigits = (secondDot >= 0 ? afterFirst.slice(0, secondDot) : afterFirst)
        .replace(/\D/g, '')
        .slice(0, ODOMETER_FRAC_MAX);
    if (!fracDigits.length && s.endsWith('.')) {
        return intPart + '.';
    }
    return fracDigits.length ? intPart + '.' + fracDigits : intPart;
};

const isValidOdometerNumber = (raw) => {
    const t = (raw ?? '').toString().trim();
    if (!t || t === '.') return false;
    const n = Number(t);
    if (Number.isNaN(n) || n < 0) return false;
    const [intStr = '', dec = ''] = t.split('.');
    if (intStr.length > ODOMETER_INT_MAX) return false;
    if (dec.length > ODOMETER_FRAC_MAX) return false;
    return true;
};

/** Whole numbers show as e.g. 123.00; already-decimal values use two fraction digits. */
const formatOdometerDisplay = (value) => {
    const s = (value ?? '').toString().trim();
    if (s === '' || s === '-') return s || '-';
    const n = Number(s);
    if (Number.isNaN(n)) return s;
    return n.toFixed(ODOMETER_FRAC_MAX);
};

const CAPACITY_MAX_DIGITS = 3;
const CAPACITY_MAX = 999;

const sanitizeCapacityInput = (raw) =>
    (raw ?? '').toString().replace(/\D/g, '').slice(0, CAPACITY_MAX_DIGITS);

const isValidCapacityNumber = (raw) => {
    const t = (raw ?? '').toString().trim();
    if (!t) return false;
    const n = Number(t);
    return /^\d+$/.test(t) && Number.isSafeInteger(n) && n >= 1 && n <= CAPACITY_MAX;
};

const VehicleMaster = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const REG_NO_RE = /^[A-Za-z0-9-]{6,20}$/;
    /** Allows VIN-style chassis plus suffix segments (e.g. MAT828022N2D07795-II/UNO/24). */
    const CHASSIS_RE = /^[A-Za-z0-9/-]{6,70}$/;

    const [vehicles, setVehicles] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [addVehicle, setAddVehicle] = useState(false);
    const [editeVehicle, setEditVehicle] = useState(false);
    const [editingVehicleId, setEditingVehicleId] = useState('');
    const [editRegistrationNo, setEditRegistrationNo] = useState('');
    const [editChassisNumber, setEditChassisNumber] = useState('');
    const [editCapacity, setEditCapacity] = useState('');
    const [editInitialOdometer, setEditInitialOdometer] = useState('');
    const [switchToNewOdometer, setSwitchToNewOdometer] = useState(false);
    const [editNewOdometer, setEditNewOdometer] = useState('');
    const [editErrors, setEditErrors] = useState({
        registration_number: '',
        chassis_number: '',
        capacity: '',
        odometer_reading: '',
        new_odometer_reading: '',
    });
    const [savingEditVehicle, setSavingEditVehicle] = useState(false);
    const [vehicleRegNo, setVehicleRegNo] = useState('');
    const [chassisNumber, setChassisNumber] = useState('');
    const [capacity, setCapacity] = useState('');
    const [odometerReading, setOdometerReading] = useState('');
    const [addErrors, setAddErrors] = useState({
        registration_number: '',
        chassis_number: '',
        capacity: '',
        odometer_reading: '',
    });
    const [savingVehicle, setSavingVehicle] = useState(false);
    const [statusUpdatingId, setStatusUpdatingId] = useState('');
    const [successModal, setSuccessModal] = useState({ open: false, message: '' });
    const openMasterSuccess = (message) =>
        setSuccessModal({ open: true, message: message || 'Saved successfully' });
    const closeMasterSuccess = () => setSuccessModal({ open: false, message: '' });

    const [itemsPerPage, setItemsPerPage] = useState(10);

    const fetchVehicleList = async () => {
        setListLoading(true);
        try {
            const response = await handleApiCall(
                'get',
                `${endpoint.listOfVehicle}?page=${currentPage}&limit=${itemsPerPage}&search=${encodeURIComponent(clampSearchInput(search).trim())}`,
                {}
            );
            if (response?.data?.code === 200) {
                const d = response?.data?.data || {};
                const list = (d?.vehicle_data || []).map((v) => ({
                    id: v?._id || v?.id,
                    vehicle: v?.registration_number || v?.vehicle || '-',
                    chassisNumber: v?.chassis_number || '-',
                    capacity: v?.capacity ?? '-',
                    odometerReading: `${v?.odometer_reading ?? '-'}`,
                    statusCode: v?.status === 1 ? 1 : 0,
                    status: v?.status === 1 ? 'Active' : 'Inactive',
                }));
                setVehicles(list);
                setTotalRecords(d?.total ?? list.length);
                setTotalPages(Math.max(1, d?.total_pages ?? 1));
            } else {
                if (response?.__apiAlertHandled || response?.__sessionExpired) return;
                const errMsg = getApiErrorMessage(response, 'Failed to load vehicles');
                if (errMsg) notifyError(errMsg);
            }
        } catch {
            notifyError('Failed to load vehicles');
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        fetchVehicleList();
    }, [currentPage, itemsPerPage, search]);

    const resetAddVehicleForm = () => {
        setVehicleRegNo('');
        setChassisNumber('');
        setCapacity('');
        setOdometerReading('');
        setAddErrors({
            registration_number: '',
            chassis_number: '',
            capacity: '',
            odometer_reading: '',
        });
    };

    const openAddVehicle = () => {
        resetAddVehicleForm();
        setAddVehicle(true);
    };

    useEffect(() => {
        if (searchParams.get('openAdd') !== '1') return;
        openAddVehicle();
    }, [searchParams]);

    const closeAddVehicle = () => {
        setAddVehicle(false);
        resetAddVehicleForm();
        if (searchParams.get('openAdd') === '1') {
            const next = new URLSearchParams(searchParams);
            next.delete('openAdd');
            setSearchParams(next, { replace: true });
        }
    };

    const openEditVehicle = (vehicle) => {
        setEditingVehicleId(vehicle?.id || '');
        setEditRegistrationNo(vehicle?.vehicle || '');
        setEditChassisNumber(vehicle?.chassisNumber || '');
        setEditCapacity(vehicle?.capacity !== '-' ? `${vehicle?.capacity ?? ''}` : '');
        setEditInitialOdometer(`${vehicle?.odometerReading ?? ''}`);
        setSwitchToNewOdometer(false);
        setEditNewOdometer('');
        setEditVehicle(true);
    };

    const closeEditVehicle = () => {
        setEditVehicle(false);
        setEditingVehicleId('');
        setEditRegistrationNo('');
        setEditChassisNumber('');
        setEditCapacity('');
        setEditInitialOdometer('');
        setSwitchToNewOdometer(false);
        setEditNewOdometer('');
        setEditErrors({
            registration_number: '',
            chassis_number: '',
            capacity: '',
            odometer_reading: '',
            new_odometer_reading: '',
        });
    };

    const validateEditVehicle = () => {
        const e = {
            registration_number: '',
            chassis_number: '',
            capacity: '',
            odometer_reading: '',
            new_odometer_reading: '',
        };
        const reg = (editRegistrationNo || '').trim().toUpperCase();
        const ch = (editChassisNumber || '').trim().toUpperCase();
        const capacityRaw = (editCapacity || '').toString().trim();
        const vehicleCapacity = Number(capacityRaw);
        const oldOdoRaw = (editInitialOdometer || '').toString().trim();
        const newOdoRaw = (editNewOdometer || '').toString().trim();
        const raw = switchToNewOdometer ? newOdoRaw : oldOdoRaw;
        const odo = Number(raw);

        if (!editingVehicleId) e.odometer_reading = 'Vehicle id is missing';

        if (!reg) e.registration_number = 'Registration number is required';
        else if (!REG_NO_RE.test(reg)) e.registration_number = 'Enter a valid registration number';

        if (!ch) e.chassis_number = 'Chassis number is required';
        else if (!CHASSIS_RE.test(ch)) {
            e.chassis_number =
                'Chassis must be 6–70 characters (letters, numbers, hyphens and slashes only)';
        }

        if (!capacityRaw) e.capacity = 'Capacity is required';
        else if (!isValidCapacityNumber(capacityRaw)) e.capacity = `Capacity must be between 1 and ${CAPACITY_MAX}`;

        if (!switchToNewOdometer) {
            if (!oldOdoRaw) e.odometer_reading = 'Initial odometer reading is required';
        } else if (!newOdoRaw) {
            e.new_odometer_reading = 'Initial new odometer reading is required';
        }

        if (raw && !isValidOdometerNumber(raw)) {
            const odoMsg = `Enter a valid odometer (max ${ODOMETER_INT_MAX} digits, optional . and ${ODOMETER_FRAC_MAX} decimals)`;
            if (switchToNewOdometer) e.new_odometer_reading = odoMsg;
            else e.odometer_reading = odoMsg;
        }

        return {
            errors: e,
            payload: {
                vehicle_id: editingVehicleId,
                registration_number: reg,
                chassis_number: ch,
                capacity: vehicleCapacity,
                odometer_reading: odo,
            },
        };
    };

    const handleEditVehicleSubmit = async (e) => {
        e?.preventDefault?.();
        if (savingEditVehicle) return;
        const { errors, payload } = validateEditVehicle();
        setEditErrors(errors);
        if (
            errors.registration_number ||
            errors.chassis_number ||
            errors.capacity ||
            errors.odometer_reading ||
            errors.new_odometer_reading
        ) {
            return;
        }

        setSavingEditVehicle(true);
        try {
            const response = await handleApiCall('put', endpoint.editVehicle, payload);
            if (response?.data?.code === 200) {
                openMasterSuccess(response?.data?.message || 'Vehicle updated successfully');
                closeEditVehicle();
                await fetchVehicleList();
                return;
            }
            if (response?.__apiAlertHandled || response?.__sessionExpired) return;
            const errMsg = getApiErrorMessage(response, 'Could not update vehicle');
            if (errMsg) notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setSavingEditVehicle(false);
        }
    };

    const validateAddVehicle = () => {
        const e = {
            registration_number: '',
            chassis_number: '',
            capacity: '',
            odometer_reading: '',
        };

        const reg = (vehicleRegNo || '').trim().toUpperCase();
        const ch = (chassisNumber || '').trim().toUpperCase();
        const capacityRaw = (capacity || '').toString().trim();
        const vehicleCapacity = Number(capacityRaw);
        const odoRaw = (odometerReading || '').toString().trim();
        const odo = Number(odoRaw);

        if (!reg) e.registration_number = 'Registration number is required';
        else if (!REG_NO_RE.test(reg)) e.registration_number = 'Enter a valid registration number';

        if (!ch) e.chassis_number = 'Chassis number is required';
        else if (!CHASSIS_RE.test(ch)) {
            e.chassis_number =
                'Chassis must be 6–70 characters (letters, numbers, hyphens and slashes only)';
        }

        if (!capacityRaw) e.capacity = 'Capacity is required';
        else if (!isValidCapacityNumber(capacityRaw)) e.capacity = `Capacity must be between 1 and ${CAPACITY_MAX}`;

        if (!odoRaw) e.odometer_reading = 'Odometer reading is required';
        else if (!isValidOdometerNumber(odoRaw)) {
            e.odometer_reading = `Enter a valid odometer (max ${ODOMETER_INT_MAX} digits, optional . and ${ODOMETER_FRAC_MAX} decimals)`;
        }

        return {
            errors: e,
            payload: {
                registration_number: reg,
                chassis_number: ch,
                capacity: vehicleCapacity,
                odometer_reading: odo,
            },
        };
    };

    const handleAddVehicleSubmit = async (e) => {
        e.preventDefault();
        if (savingVehicle) return;
        const { errors, payload } = validateAddVehicle();
        setAddErrors(errors);
        if (errors.registration_number || errors.chassis_number || errors.capacity || errors.odometer_reading) return;

        setSavingVehicle(true);
        try {
            const response = await handleApiCall('post', endpoint.addVehicle, payload);
            if (response?.data?.code === 200) {
                openMasterSuccess(response?.data?.message || 'Vehicle added successfully');
                closeAddVehicle();
                setCurrentPage(1);
                await fetchVehicleList();
                return;
            }
            if (response?.__apiAlertHandled || response?.__sessionExpired) return;
            const errMsg = getApiErrorMessage(response, 'Could not add vehicle');
            if (errMsg) notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setSavingVehicle(false);
        }
    };

    const handleVehicleStatusToggle = async (vehicleId, currentStatus) => {
        const nextStatus = currentStatus === 1 ? 0 : 1;
        if (!vehicleId) return;
        setStatusUpdatingId(vehicleId);
        try {
            const response = await handleApiCall('put', endpoint.updateStatusVehicle, {
                vehicle_id: vehicleId,
                status: nextStatus,
            });
            if (response?.data?.code === 200) {
                setVehicles((prev) =>
                    prev.map((v) =>
                        v.id === vehicleId
                            ? {
                                ...v,
                                statusCode: nextStatus,
                                status: nextStatus === 1 ? 'Active' : 'Inactive',
                            }
                            : v
                    )
                );
                notifySuccess(response?.data?.message || 'Vehicle status updated');
                return;
            }
            if (response?.__apiAlertHandled || response?.__sessionExpired) return;
            const errMsg = getApiErrorMessage(response, 'Could not update vehicle status');
            if (errMsg) notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setStatusUpdatingId('');
        }
    };

    // 🔍 Local search on current page results
    const filteredVehicles = vehicles.filter((v) =>
        `${v.vehicle} ${v.chassisNumber} ${v.capacity} ${v.odometerReading}`.toLowerCase().includes(search.toLowerCase())
    );
    const displayFrom = totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const displayTo = Math.min(currentPage * itemsPerPage, totalRecords);

    return (
        <>
        <Toastify/>
            <div className="tab-pane-content md:mt-7 mt-3">

                <div className='flex gap-x-2.5 md:mb-4 mb-3'>
                    
                    <input
                        type="text"
                        placeholder="Search..."
                        className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm"
                        value={search}
                        maxLength={SEARCH_MAX_LENGTH}

                        onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                    />

                    <PermissionGate module={MODULES.MASTER} action="add">
                        <button className="btn btn-danger ml-auto" onClick={openAddVehicle}>
                            + Add New
                        </button>
                    </PermissionGate>
                </div>

                {/* Table */}
                <div className="common-table-wrapper m-0">
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Vehicle</th>
                                    <th>Chassis Number</th>
                                    <th>Vehicle Fuel Capacity</th>
                                    <th>Odometer Reading (Km)</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody className='max-[767px]:[&_td]:!py-1.5'>
                                {listLoading && (
                                    <tr>
                                        <td colSpan={7} className="text-center py-4 text-gray-500">
                                            Loading...
                                        </td>
                                    </tr>
                                )}
                                {!listLoading && filteredVehicles.map((driver, index) => (
                                    <tr key={driver.id || index}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>{driver.vehicle}</td>
                                        <td>{driver.chassisNumber}</td>
                                        <td>{driver.capacity}</td>
                                        <td>{formatOdometerDisplay(driver.odometerReading)}</td>
                                        <td>{driver.status}</td>
                                        <td>
                                            <div className="flex items-center justify-center gap-2">
                                                <PermissionGate module={MODULES.MASTER} action="edit">
                                                    <label className="relative inline-flex items-center cursor-pointer w-9 min-w-9 h-5 bg-[#E11818] rounded-full my-0 [&:has(input:checked)]:bg-[#17B517] [&:has(input:checked)_.absolute]:!left-[17px] transition-all">
                                                        <input
                                                            type="checkbox"
                                                            className="hidden"
                                                            checked={driver.statusCode === 1}
                                                            onChange={() => handleVehicleStatusToggle(driver.id, driver.statusCode)}
                                                            disabled={statusUpdatingId === driver.id || listLoading}
                                                        />
                                                        <div className="absolute left-[3px] top-0.5 w-4 h-4 bg-white rounded-full transition-all"></div>
                                                    </label>
                                                </PermissionGate>
                                                <PermissionGate module={MODULES.MASTER} action="edit">
                                                    <span onClick={() => openEditVehicle(driver)}> <img src={EditIcon} alt="Edit" className='h-7 min-w-[22px]'/></span>
                                                </PermissionGate>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!listLoading && filteredVehicles.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="text-center py-4 text-gray-500">
                                            No vehicles found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 📄 Pagination */}
                <div className="flex gap-x-4 items-center md:mt-4 mt-3">

                    <select
                        className='p-2 shadow-sm outline-none max-[767px]:text-xs'
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                    {/* Total Records */}
                    <div className="text-xs md:text-sm text-gray-500 ml-auto">
                        {listLoading
                            ? '—'
                            : totalRecords === 0
                                ? '0 records found'
                                : `${displayFrom}-${displayTo} of ${totalRecords} records found`}
                    </div>
                    {/* Pagination Controls */}
                    <div className="flex items-center gap-2 md:pr-8">
                        <button
                            type="button"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={listLoading || currentPage === 1}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage===1 ? '' : 'cursor-pointer'}`}
                        >
                            <img src={LeftAngle} className='invert grayscale brightness-100'/>
                        </button>
                        <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                        <span className="text-sm text-gray-600 text-nowrap">
                            of {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={listLoading || currentPage === totalPages || totalRecords === 0}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage===1 ? '' : 'cursor-pointer'}`}
                        >
                            <img src={RightAngle} className='invert grayscale brightness-100'/>
                        </button>

                    </div>
                </div>

            </div>

            {/* Add New Copmany Modal */}
            {addVehicle && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] max-[767px]:px-4">
                    <div className="bg-white p-6 rounded-xl w-full max-w-96">
                        <h2 className="mb-4 text-center !text-xl font-UbuntuBold">Add New Vehicle</h2>
                        <form
                            className='font-UbuntuRegular [&_label]:mb-1.5 [&_label]:font-UbuntuMedium'
                            onSubmit={handleAddVehicleSubmit}
                            noValidate
                        >
                            <div className='form-group mb-3'>
                                <label htmlFor='add-registration-number'>Add Vehicle Registration</label>
                                <input
                                    id='add-registration-number'
                                    className='form-control'
                                    value={vehicleRegNo}
                                    onChange={(e) => {
                                        setVehicleRegNo(e.target.value);
                                        if (addErrors.registration_number) {
                                            setAddErrors((prev) => ({ ...prev, registration_number: '' }));
                                        }
                                    }}
                                    disabled={savingVehicle}
                                />
                                {addErrors.registration_number && (
                                    <p className='text-danger small mt-1 mb-0'>{addErrors.registration_number}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label htmlFor='add-chassis-number'>Add Chassis Number</label>
                                <input
                                    id='add-chassis-number'
                                    className='form-control'
                                    value={chassisNumber}
                                    onChange={(e) => {
                                        setChassisNumber(e.target.value);
                                        if (addErrors.chassis_number) {
                                            setAddErrors((prev) => ({ ...prev, chassis_number: '' }));
                                        }
                                    }}
                                    disabled={savingVehicle}
                                />
                                {addErrors.chassis_number && (
                                    <p className='text-danger small mt-1 mb-0'>{addErrors.chassis_number}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label htmlFor='add-capacity'>Vehicle Fuel Capacity</label>
                                <input
                                    id='add-capacity'
                                    className='form-control'
                                    type='text'
                                    inputMode='numeric'
                                    autoComplete='off'
                                    maxLength={CAPACITY_MAX_DIGITS}
                                    value={capacity}
                                    onChange={(e) => {
                                        setCapacity(sanitizeCapacityInput(e.target.value));
                                        if (addErrors.capacity) {
                                            setAddErrors((prev) => ({ ...prev, capacity: '' }));
                                        }
                                    }}
                                    disabled={savingVehicle}
                                />
                                {addErrors.capacity && (
                                    <p className='text-danger small mt-1 mb-0'>{addErrors.capacity}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label htmlFor='add-odometer-reading'>Initial Odometer Reading</label>
                                <input
                                    id='add-odometer-reading'
                                    className='form-control'
                                    type='text'
                                    inputMode='decimal'
                                    autoComplete='off'
                                    maxLength={ODOMETER_INPUT_MAX_LEN}
                                    value={odometerReading}
                                    onChange={(e) => {
                                        setOdometerReading(sanitizeOdometerInput(e.target.value));
                                        if (addErrors.odometer_reading) {
                                            setAddErrors((prev) => ({ ...prev, odometer_reading: '' }));
                                        }
                                    }}
                                    disabled={savingVehicle}
                                />
                                {addErrors.odometer_reading && (
                                    <p className='text-danger small mt-1 mb-0'>{addErrors.odometer_reading}</p>
                                )}
                            </div>
                            <div className="flex justify-center gap-3 mt-3">
                                <button
                                    type='button'
                                    className="btn btn-primary flex-1 max-[767px]:!py-2.5"
                                    onClick={closeAddVehicle}
                                    disabled={savingVehicle}
                                >
                                    Cancel
                                </button>

                                <button
                                    type='submit'
                                    className="btn btn-danger flex-1 max-[767px]:!py-2.5"
                                    disabled={savingVehicle}
                                >
                                    {savingVehicle ? 'Saving...' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Copmany Modal */}
            {editeVehicle && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] max-[767px]:px-4">
                    <div className="bg-white p-6 rounded-xl w-full max-w-96">
                        <h2 className="mb-4 text-center !text-xl font-UbuntuBold">Edit Vehicle Details</h2>
                        <form
                            className='font-UbuntuRegular [&_label]:mb-1.5 [&_label]:font-UbuntuMedium'
                            onSubmit={handleEditVehicleSubmit}
                            noValidate
                        >
                            <div className='form-group mb-3'>
                                <label htmlFor='edit-registration'>Add Vehicle Registration</label>
                                <input
                                    id='edit-registration'
                                    className='form-control bg-light'
                                    readOnly
                                    value={editRegistrationNo}
                                    onChange={(e) => {
                                        setEditRegistrationNo(e.target.value);
                                        if (editErrors.registration_number) {
                                            setEditErrors((prev) => ({ ...prev, registration_number: '' }));
                                        }
                                    }}
                                    disabled={savingEditVehicle}
                                />
                                {editErrors.registration_number && (
                                    <p className='text-danger small mt-1 mb-0'>{editErrors.registration_number}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label htmlFor='edit-chassis'>Add Chassis Number</label>
                                <input
                                    id='edit-chassis'
                                    className='form-control bg-light'
                                    readOnly
                                    value={editChassisNumber}
                                    onChange={(e) => {
                                        setEditChassisNumber(e.target.value);
                                        if (editErrors.chassis_number) {
                                            setEditErrors((prev) => ({ ...prev, chassis_number: '' }));
                                        }
                                    }}
                                    disabled={savingEditVehicle}
                                />
                                {editErrors.chassis_number && (
                                    <p className='text-danger small mt-1 mb-0'>{editErrors.chassis_number}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label htmlFor='edit-capacity'>Vehicle Fuel Capacity</label>
                                <input
                                    id='edit-capacity'
                                    className='form-control'
                                    type='text'
                                    inputMode='numeric'
                                    autoComplete='off'
                                    maxLength={CAPACITY_MAX_DIGITS}
                                    value={editCapacity}
                                    onChange={(e) => {
                                        setEditCapacity(sanitizeCapacityInput(e.target.value));
                                        if (editErrors.capacity) {
                                            setEditErrors((prev) => ({ ...prev, capacity: '' }));
                                        }
                                    }}
                                    disabled={savingEditVehicle}
                                />
                                {editErrors.capacity && (
                                    <p className='text-danger small mt-1 mb-0'>{editErrors.capacity}</p>
                                )}
                            </div>
                            <div className='form-group mb-3'>
                                <label htmlFor='edit-initial-odo'>Initial Odometer Reading</label>
                                <div>
                                    <input
                                        id='edit-initial-odo'
                                        className={`form-control bg-light`}
                                        type='text'
                                        inputMode='decimal'
                                        autoComplete='off'
                                        maxLength={ODOMETER_INPUT_MAX_LEN}
                                        value={editInitialOdometer}
                                        onChange={(e) => {
                                            setEditInitialOdometer(sanitizeOdometerInput(e.target.value));
                                            if (editErrors.odometer_reading) {
                                                setEditErrors((prev) => ({ ...prev, odometer_reading: '' }));
                                            }
                                        }}
                                        readOnly
                                        // readOnly={switchToNewOdometer}
                                        disabled={savingEditVehicle}
                                    />
                                    {editErrors.odometer_reading && (
                                        <p className='text-danger small mt-1 mb-0'>{editErrors.odometer_reading}</p>
                                    )}
                                    <div className='flex justify-end mt-2'>
                                        <small className='mr-2'>Switch To New Odometer</small>
                                        <label className="relative inline-flex items-center cursor-pointer w-9 min-w-9 h-5 bg-[#E11818] rounded-full my-0 [&:has(input:checked)]:bg-[#17B517] [&:has(input:checked)_.absolute]:!left-[17px] transition-all">
                                            <input
                                                type="checkbox"
                                                className="hidden"
                                                checked={switchToNewOdometer}
                                                onChange={(e) => {
                                                    setSwitchToNewOdometer(e.target.checked);
                                                    setEditErrors((prev) => ({
                                                        ...prev,
                                                        odometer_reading: '',
                                                        new_odometer_reading: '',
                                                    }));
                                                }}
                                                disabled={savingEditVehicle}
                                            />
                                            <div className="absolute left-[3px] top-0.5 w-4 h-4 bg-white rounded-full transition-all"></div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {switchToNewOdometer && (
                                <div className='form-group mb-3'>
                                    <label htmlFor='edit-new-odo'>Initial New Odometer Reading</label>
                                    <input
                                        id='edit-new-odo'
                                        className='form-control'
                                        type='text'
                                        inputMode='decimal'
                                        autoComplete='off'
                                        maxLength={ODOMETER_INPUT_MAX_LEN}
                                        value={editNewOdometer}
                                        onChange={(e) => {
                                            setEditNewOdometer(sanitizeOdometerInput(e.target.value));
                                            if (editErrors.new_odometer_reading) {
                                                setEditErrors((prev) => ({ ...prev, new_odometer_reading: '' }));
                                            }
                                        }}
                                        disabled={savingEditVehicle}
                                    />
                                    {editErrors.new_odometer_reading && (
                                        <p className='text-danger small mt-1 mb-0'>{editErrors.new_odometer_reading}</p>
                                    )}
                                </div>
                            )}
                            <div className="flex justify-center gap-3 mt-3">
                                <button
                                    type='button'
                                    className="btn btn-primary flex-1 max-[767px]:!py-2.5"
                                    onClick={closeEditVehicle}
                                    disabled={savingEditVehicle}
                                >
                                    Cancel
                                </button>

                                <button
                                    type='submit'
                                    className="btn btn-danger flex-1 max-[767px]:!py-2.5"
                                    disabled={savingEditVehicle}
                                >
                                    {savingEditVehicle ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <MasterSuccessModal
                open={successModal.open}
                message={successModal.message}
                onClose={closeMasterSuccess}
            />
        </>
    );
};

export default VehicleMaster;